/**
 * The "big kit" fields of a tower kit (2026-10-21, owner: "All skins should go beyond just a recolour … an
 * improvement over the base and ideally have unique animations"): a kit may HIDE stock parts (kit/hide.ts), FIT a
 * replacement piece to each tower's own measured roof / drum / plinth (kit/fit.ts), SWAP the whole tower model for its
 * own `tower_<id>_t<n>__<model>.glb` (kit/towerModels.ts), and animate its pieces with orbits, flaps, flows and an
 * attack-synced charge (kit/kitMaterial.ts, kit/charge.ts). Pure data; tools/art/skins/README.md "Big kits" is the
 * authoring guide.
 */

/**
 * Stock parts a kit may hide (kit/hide.ts `HIDE_PARTS` maps the model parts to these). Hidden vertices collapse in the
 * body material's vertex shader and its shadow depth material: no draw, no fragment, no shadow, no CPU. The element
 * parts and every `_glow` mesh are never hidden (readability), nor the drum or the weapon.
 *  - `roof`     the roof part: cone, pavilion posts and finial (on the archer and poison the whole upper storey; the
 *               cannon / ballista / fire / mortar back roofs; the barracks' hall roofs; the arcane collar; the hex
 *               feather crown; the beacon's orb cage). Frost has none (its crystal is the element).
 *  - `flags`    the painted cloth (flags, banners, wall hangings). The team pennant keeps flying (its own material).
 *  - `gallery`  the deck/gallery timber, where nothing stands on it (kit/hide.ts HIDE_REFUSED).
 *  - `scaffold` the build scaffold (shown only while `build` plays).
 *  - `lamps`    the lit lamp fittings (their glowing flames stay: they are glow meshes).
 *  - `doors`    the barracks' gates.
 *  - `plinth`   the stone plinth under the drum (fit a new base with `fitTo: "plinth"`).
 */
export type HideGroup = "roof" | "flags" | "gallery" | "scaffold" | "lamps" | "doors" | "plinth";
/** In bit order: a group's bit in the `tdHide` vertex attribute and the hide mask is its index + 1 (0 = never hidden). */
export const HIDE_GROUPS: readonly HideGroup[] = ["roof", "flags", "gallery", "scaffold", "lamps", "doors", "plinth"];

/**
 * The envelopes a piece can be fitted to (tools/art/skins/sockets.mjs measures them per model into anchors.json
 * `dims`): `roof` the roof part, `drum` the masonry drum, `plinth` the base slab, `gallery` the deck, `tower` the whole
 * model. A model without the part (frost has no roof) skips the use.
 */
export type FitMode = "roof" | "drum" | "plinth" | "gallery" | "tower";
export const FIT_MODES: readonly FitMode[] = ["roof", "drum", "plinth", "gallery", "tower"];

/**
 * A fitted placement (kit/fit.ts): the piece is authored in a UNIT envelope — 1 m across (radius 0.5 about its origin),
 * 1 m tall, origin at the bottom centre — and is scaled to the envelope `to` measured on each tower × tier, so one piece
 * fits all 12 silhouettes. Then the use's own `scale` (uniform, [t1, t2, t3]), `offset` (metres) and `yaw` apply.
 */
export interface FitSpec {
  readonly to: FitMode;
  /**
   * The piece's height: `fit` the envelope's height (default; cladding, a pavilion), `keep` the piece's proportions
   * (its height scales like its width: a dome stays a dome), `min` the lower of the two (a cap that never stretches
   * tall nor overflows a low roof).
   */
  readonly height?: "fit" | "keep" | "min";
  /** Grow the radius by this fraction (0.08 = 8 % proud of the surface: cladding that must clear the stonework). */
  readonly margin?: number;
  /**
   * `round` (default): one radius, the part's widest, the piece faces the game camera; `box`: x and z scaled to the
   * part's own half-extents in model axes (a square keep, the cannon's oblong back roof).
   */
  readonly shape?: "round" | "box";
  /**
   * Follow the part's taper: the radius runs from the envelope's bottom radius `r0` to its top radius `r1` up the
   * piece (battered drum walls, a flared plinth). Round only.
   */
  readonly taper?: boolean;
}

/**
 * Orbit: the piece circles the use's mount point about its up axis (shards round the apex, a gear ring round the eave),
 * `radius` out — metres on a socket use; on a fitted use, unit-envelope units (0.5 = the envelope's own radius), so the
 * ring follows each tower's size; both are multiplied by the use's `scale` — at `speed` rad/s (negative: the other
 * way), the orbit plane tilted `tilt` degrees about the right axis. `copies` (1–8) evenly spaced copies share the orbit,
 * built once into the geometry: one draw, triangles × copies. The radius is baked into the geometry and the GPU turns
 * it; `roll` still turns each copy about its own centre (a gear rolling round), `spin` would add to the orbit.
 */
export interface Orbit { readonly radius: number; readonly speed: number; readonly tilt?: number; readonly copies?: number }

/**
 * Flap: the piece rocks about a hinge (a banner on its pole, wings, a shutter): `deg` amplitude, `bias` degrees of rest
 * angle, `hz` its own rate (default the motion's `hz`), about `axis` (`out` default: wings beating up and down;
 * `up`: a door or banner swinging; `right`: a lid). `mirror`: vertices left of the origin turn the other way (both
 * wings of one piece), hinged `hinge` metres either side of the origin along right.
 * `drive: "charge"`: no steady sine; the angle follows the tower's attack (kit/charge.ts `strikeLevel`): `bias` is the
 * rest (on the anvil), `bias + deg` the top. The piece lifts slowly as the next shot nears, holds at the top, slams down
 * in the last 0.08 s so it lands exactly on the round's release, rebounds a little and rests; idle towers rest. It
 * shares the charge's per-tower material clone (`uKitStrike`) and freezes at rest under Reduced effects.
 */
export interface Flap { readonly deg: number; readonly bias?: number; readonly hz?: number; readonly axis?: "out" | "up" | "right"; readonly mirror?: boolean; readonly hinge?: number; readonly drive?: "charge" }
export const FLAP_AXES = ["out", "right", "up"] as const;

/**
 * Flow: a slow ripple travelling along the piece (lava in a channel, water down a spout, a rune line filling): bands
 * `length` metres apart moving `speed` m/s `along` the piece's axis; each band brightens its emissive by `glow` (a
 * fraction: only emissive palette cells glow) and swells the surface `bulge` metres along its normal.
 */
export interface Flow { readonly speed: number; readonly length: number; readonly along?: "up" | "down" | "right" | "left" | "out"; readonly glow?: number; readonly bulge?: number }
export const FLOW_AXES = ["up", "down", "right", "left", "out"] as const;

/**
 * Charge: the piece answers the tower's attack (kit/charge.ts): its emissive rises `glow` × (×(1 + glow) at the peak)
 * and it swells by `pulse` (fraction) as the next shot nears, peaking on the shot's release, then falls back. Idle
 * towers rest at 0. One material clone per dressed tower carries its charge (the program is shared).
 */
export interface Charge { readonly glow?: number; readonly pulse?: number }

/**
 * A whole replacement model for a tower kit: `tower_<id>_t<n>__<model>.glb` (and `tower_<id>_t4_<branch>__<model>` for
 * tier IV), with the stock model's node names (`<model name>.<part>`: weapon, muzzle parts, crystal, flags …) and
 * clips (`build idle fire`, plus the tower's own); used wherever the manifest ships it, else the stock model + kit.
 * `onModel` says which of the kit's layers still go on a shipped model (default none: the model is the look; the
 * idle and flourish always play).
 */
export interface ModelLayers { readonly slots?: boolean; readonly attachments?: boolean; readonly hide?: boolean }

/**
 * A skin model's surface, set at load on its baked materials (kit/modelSurface.ts): what the bake cannot know about
 * the game's lights. All optional.
 */
export interface ModelSurface {
  /**
   * The lowest roughness the lit atlas may draw with. A flat glossy plate under the game's directional key and fill
   * lights is one uniform highlight from edge to edge — a flat sun-beige or sky-blue sheet — so a glass skin keeps
   * its gloss off the big flat faces by raising the floor.
   */
  readonly roughnessFloor?: number;
  /** A fresnel edge glint added to the lit atlas: linear colour 0xRRGGBB times `strength` (HDR), power `power` (default 3). */
  readonly glint?: { readonly colour: number; readonly strength: number; readonly power?: number };
  /** Multiplies the model's own lights (`_skin_glow`), linear RGB, per tower type (`*` for every tower). */
  readonly skinGlow?: Readonly<Record<string, readonly [number, number, number]>>;
}
